/**
 * ReviewIncidentPropertiesPage
 *
 * Page for reviewing incident properties for premises liability cases.
 * Each property coverage can be independently marked as APPROVED or N/A.
 * At least one must be approved before continuing.
 *
 * Workflow: [Review Incident Properties] -> Research Property & Owners -> Send LOR
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { Building2, MapPin, RotateCcw, Users } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CoverageStatusBadge } from '@/components/coverage/CoverageStatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useGetCoverageQuery,
  useGetCoveragesQuery,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import {
  ReadOrganizationWithEmployees,
  ReadPersonWithRelationships,
  ReadPropertyWithOwners,
} from '@/services/api/generated/subjectsService';
import { useAllSubjectsWithRelationshipsQuery } from '@/services/api/subjectsService';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api/tasksService';
import {
  CoverageCategory,
  CoverageSchema,
  CoverageStatus,
} from '@/services/types/coverage-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { ClaimFilingTopHeader } from '../common/ClaimFilingTopHeader';
import { PremisesStepper } from './PremisesStepper';

/**
 * Get display name from a party that can be either a Person or Organization.
 * - Person: combines first_name and last_name
 * - Organization: uses legal_name or dba_name
 */
const getPartyDisplayName = (
  party: ReadPersonWithRelationships | ReadOrganizationWithEmployees | null | undefined,
): string => {
  if (!party) return 'Unknown';

  // Check if it's an organization (has organization_type or legal_name)
  if ('organization_type' in party || 'legal_name' in party) {
    const org = party as ReadOrganizationWithEmployees;
    return org.legal_name || org.dba_name || 'Unknown Organization';
  }

  // It's a person
  const person = party as ReadPersonWithRelationships;
  const fullName = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
  return fullName || 'Unknown Person';
};

export const ReviewIncidentPropertiesPage: React.FC = () => {
  const { matter_id: matterId, coverage_id: initialCoverageId } = useParams<{
    matter_id: string;
    coverage_id: string;
  }>();
  const navigate = useNavigate();

  // Fetch coverages to find property coverages
  const { data: coverages, refetch: refetchCoverages } = useGetCoveragesQuery(
    { matterId: matterId!, includeShared: true },
    {
      skip: !matterId,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: currentCoverageFromApi } = useGetCoverageQuery(
    matterId && initialCoverageId
      ? { matterId, coverageId: initialCoverageId }
      : skipToken,
    { skip: !matterId || !initialCoverageId },
  );

  // Fetch tasks to find the review/confirm task
  const { data: tasksResponse } = useGetTasksQuery(
    { matter: matterId!, limit: 200 },
    { skip: !matterId },
  );

  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();

  const [updateCoverage, { isLoading: isUpdatingCoverage }] =
    useUpdateCoverageMutation();
  const [updateTask] = useUpdateTaskMutation();

  // Fetch all subjects with relationships to get property details
  const { data: subjectsData } = useAllSubjectsWithRelationshipsQuery(
    { matterId: matterId! },
    { skip: !matterId },
  );

  // Find all property coverages
  const propertyCoverages = React.useMemo(() => {
    const allPropertyCoverages = (coverages || []).filter(
      (coverage) => coverage.category === CoverageCategory.PROPERTY,
    );
    if (
      currentCoverageFromApi?.category === CoverageCategory.PROPERTY &&
      !allPropertyCoverages.some(
        (coverage) => coverage.id === currentCoverageFromApi.id,
      )
    ) {
      return [currentCoverageFromApi, ...allPropertyCoverages];
    }
    return allPropertyCoverages;
  }, [coverages, currentCoverageFromApi]);

  // Map property_id to property data for quick lookup
  const propertyMap = React.useMemo(() => {
    const map = new Map<string, ReadPropertyWithOwners>();
    if (subjectsData?.properties) {
      subjectsData.properties.forEach((prop) => {
        map.set(prop.id as string, prop);
      });
    }
    return map;
  }, [subjectsData?.properties]);

  // Format physical address for display
  const formatAddress = (addr: ReadPropertyWithOwners['physical_address']) => {
    if (!addr) return null;
    const parts = [
      addr.line1,
      addr.line2,
      [addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
    ].filter(Boolean);
    return parts.join(', ') || null;
  };

  // Find the premises entry review task.
  const reviewTask = React.useMemo(() => {
    const tasks = tasksResponse?.results || [];
    return tasks.find(
      (t) =>
        t.type?.canonical_type === CanonicalTaskType.REVIEW_INCIDENT_PROPERTIES,
    );
  }, [tasksResponse]);

  // Get property details for a coverage
  const getPropertyDetails = (coverage: CoverageSchema) => {
    const linkedProperty = coverage.property_id
      ? propertyMap.get(coverage.property_id as string)
      : null;
    return {
      address: linkedProperty ? formatAddress(linkedProperty.physical_address) : null,
      propertyName: linkedProperty?.property_name || null,
      owners: linkedProperty?.owners || [],
      propertyType: linkedProperty?.real_property_type,
    };
  };

  // Get display name for a property coverage
  const getPropertyDisplayName = (coverage: CoverageSchema) => {
    const details = getPropertyDetails(coverage);
    if (details.address) {
      return details.address;
    }
    if (details.propertyName) {
      return details.propertyName;
    }
    if (coverage.named_insured && coverage.named_insured.length > 0) {
      return coverage.named_insured[0];
    }
    return 'Unknown Property Location';
  };

  // Per-coverage action handlers
  const handleApproveCoverage = async (coverageId: string) => {
    if (!matterId) return;
    try {
      await updateCoverage({
        matterId,
        coverageId,
        coverage: { status: CoverageStatus.APPROVED },
      }).unwrap();
      refetchCoverages();
    } catch {
      toast.error('Failed to approve property');
    }
  };

  const handleMarkNA = async (coverageId: string) => {
    if (!matterId) return;
    try {
      await updateCoverage({
        matterId,
        coverageId,
        coverage: { status: CoverageStatus.NOT_APPLICABLE },
      }).unwrap();
      refetchCoverages();
    } catch {
      toast.error('Failed to mark property as N/A');
    }
  };

  const handleResetCoverage = async (coverageId: string) => {
    if (!matterId) return;
    try {
      await updateCoverage({
        matterId,
        coverageId,
        coverage: { status: CoverageStatus.NEEDS_APPROVAL },
      }).unwrap();
      refetchCoverages();
    } catch {
      toast.error('Failed to reset property status');
    }
  };

  // Derived state
  const hasAtLeastOneApproved = propertyCoverages.some(
    (c) => c.status === CoverageStatus.APPROVED,
  );

  const firstApprovedCoverageId = React.useMemo(() => {
    const approved = propertyCoverages.find(
      (c) => c.status === CoverageStatus.APPROVED,
    );
    return approved?.id ?? null;
  }, [propertyCoverages]);

  // Complete review: mark task as COMPLETED, navigate to first approved property
  const handleCompleteReview = async () => {
    if (!matterId || !firstApprovedCoverageId) return;

    try {
      if (reviewTask) {
        const completedStatus = taskStatuses.find(
          (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
        );
        if (completedStatus) {
          await updateTask({
            matterId,
            taskId: reviewTask.id,
            task: {
              status_id: completedStatus.id,
              completed_at: new Date().toISOString(),
            },
          }).unwrap();
        }
      }

      toast.success('Property review completed');
      navigate(
        `/matters/${matterId}/premises/${firstApprovedCoverageId}/research-property`,
      );
    } catch {
      toast.error('Failed to complete review');
    }
  };

  const isReviewComplete =
    reviewTask?.status?.canonical_status === CanonicalTaskStatus.COMPLETED;

  // Find the specific coverage from initialCoverageId (if navigated from a card)
  const currentCoverage = propertyCoverages.find(
    (c) => c.id === initialCoverageId,
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <ClaimFilingTopHeader matterId={matterId!} />
      {initialCoverageId && (
        <PremisesStepper
          matterId={matterId!}
          coverageId={initialCoverageId}
          currentStep="confirm_location"
          coverage={currentCoverage}
        />
      )}

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Property Review */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-violet-600" />
                Review Incident Properties
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/matters/${matterId}/coverage/create`)}
              >
                Add Property Coverage
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {propertyCoverages.length > 0 ? (
              <>
                <p className="text-sm text-zinc-600 mb-4">
                  Review each property. Mark relevant properties as Approved and
                  others as N/A. At least one must be approved to continue.
                </p>
                {propertyCoverages.map((coverage) => {
                  const isApproved =
                    coverage.status === CoverageStatus.APPROVED;
                  const isNA =
                    coverage.status === CoverageStatus.NOT_APPLICABLE;
                  const needsApproval = !isApproved && !isNA;
                  const propertyDetails = getPropertyDetails(coverage);

                  return (
                    <div
                      key={coverage.id}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isApproved
                          ? 'border-green-300 bg-green-50/50'
                          : isNA
                            ? 'border-dashed border-zinc-300 bg-zinc-50/50'
                            : 'border-zinc-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          {/* Property location at the top */}
                          <p className="font-medium text-zinc-900 flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-orange-500" />
                            {getPropertyDisplayName(coverage)}
                          </p>
                          {/* Show carrier below the address */}
                          {coverage.carrier?.name && (
                            <p className="text-sm text-zinc-600 flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-zinc-400" />
                              Carrier: <span className="text-zinc-900">{coverage.carrier.name}</span>
                            </p>
                          )}
                          {/* Show property owners */}
                          {propertyDetails.owners.length > 0 && (
                            <p className="text-sm text-zinc-600 flex items-center gap-1.5">
                              <Users className="h-3.5 w-3.5 text-zinc-400" />
                              {propertyDetails.owners.length} owner{propertyDetails.owners.length !== 1 ? 's' : ''}:{' '}
                              <span className="text-zinc-900">
                                {propertyDetails.owners
                                  .slice(0, 2)
                                  .map((o) => getPartyDisplayName(o.party))
                                  .join(', ')}
                                {propertyDetails.owners.length > 2 && ` +${propertyDetails.owners.length - 2} more`}
                              </span>
                            </p>
                          )}
                          {/* Property type badge */}
                          {propertyDetails.propertyType && (
                            <Badge
                              variant="secondary"
                              className="text-[10px] bg-zinc-100 text-zinc-600 border-0 capitalize"
                            >
                              {propertyDetails.propertyType.replace('_', ' ')}
                            </Badge>
                          )}
                          {coverage.policy_identifiers?.[0] && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Policy:{' '}
                              {
                                coverage.policy_identifiers[0]
                                  .identifier_value
                              }
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <CoverageStatusBadge status={coverage.status} />
                          {needsApproval && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isUpdatingCoverage}
                                onClick={() => handleApproveCoverage(coverage.id)}
                                className="text-green-700 border-green-300 hover:bg-green-50"
                              >
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isUpdatingCoverage}
                                onClick={() => handleMarkNA(coverage.id)}
                              >
                                N/A
                              </Button>
                            </>
                          )}
                          {(isApproved || isNA) && !isReviewComplete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isUpdatingCoverage}
                              onClick={() => handleResetCoverage(coverage.id)}
                              title="Reset status"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-lg">
                <Building2 className="h-8 w-8 text-zinc-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-600 mb-3">
                  No property coverages found for this matter
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(`/matters/${matterId}/coverage/create`)
                  }
                >
                  Add Property Coverage
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => navigate(`/matters/${matterId}/coverage`)}
          >
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            {isReviewComplete ? (
              <Button
                onClick={() =>
                  navigate(
                    `/matters/${matterId}/premises/${firstApprovedCoverageId}/research-property`,
                  )
                }
                disabled={!firstApprovedCoverageId}
                className="bg-violet-600 hover:bg-violet-700"
              >
                Continue to Research Property
              </Button>
            ) : (
              <Button
                onClick={handleCompleteReview}
                disabled={
                  isUpdatingCoverage ||
                  !hasAtLeastOneApproved ||
                  propertyCoverages.length === 0
                }
                className="bg-violet-600 hover:bg-violet-700"
              >
                Complete Review & Continue
              </Button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};
